import { inject, Injectable } from "@angular/core";
import { ColumnDefinition, EntityInterface } from "./Table";
import { UserService } from '../services/user/user.service';
import { AppConstants } from "../constants/app-constants";
import { DateTime } from 'luxon';

/**
 * Invitation is an abstracted interface that contains useful helper functions when dealing
 * with invitations
 */
export class Invitation implements EntityInterface {

  /**
   * Constructor for the Invitation object
   */
  constructor(
    public email: string,
    public name: string,
    public businessName: string,
    public lastSent: string,
    public id?: string, // 'id' is an identical field to 'token' so that table searches can still have a unique identifier
    public accepted: number = 0,  // New field
  ) { }

  /**
   * Determine if two invitations are equal
   * 
   * @param invitation the invitation to compare against
   * @returns true if the invitations are equal, false otherwise
   */
  public equals(invitation: Invitation): boolean {
    return this.email === invitation.email &&
      this.lastSent === invitation.lastSent &&
      this.name === invitation.name &&
      this.businessName === invitation.businessName;
  }

  /**
   * Return a copy of this invitation
   * 
   * @returns a copy of this invitation
   */
  public copyOf(): Invitation {
    return new Invitation(
      this.email,
      this.name,
      this.businessName,
      this.lastSent,
      this.id,
      this.accepted
    );
  }

  /**
   * Return the invitation as a string representation, used for filtering/searching
   * 
   * We use a unicode full block as a delimeter, so the only string matches that
   * return are from our fields (unless the user is typing the unicode full block
   * character like a maniac)
   */
  public toString(defs?: ColumnDefinition<Invitation>[]): string {
    // If defs is not provided or isn't an array, return a default string representation.
    if (!defs || !Array.isArray(defs)) {
      // Concatenate key properties (adjust as needed)
      return `${this.name} ${this.businessName} ${this.email} ${this.lastSent}`.toLowerCase();
    }

    let str = "";
    for (let col of defs) {
      if (col.toStringTransform != undefined) {
        str += col.toStringTransform((this as any)[col.field]) + "█";
      } else {
        str += (this as any)[col.field];
      }
    }
    return str.toLowerCase();
  }

  public isAccepted(): boolean {
    return this.accepted === 1;
  }
}

/**
 * InvitationInterface is an interface for the invitation object. This handles direct
 * intreactions with the backend API
 */
export interface InvitationDto {
  id: number;
  email: string;
  name: string;
  businessName?: string; // FIXME: API accepts this but returns "business_name"
  lastSent?: string;
  accepted?: number;  // 0 for non-accepted, 1 for accepted
  role?: string;
  isDirect?: boolean;
  senderAccountId?: number;
  supplierId?: number;
}

/**
 * InvitationMapper is a service that maps between the Invitation model and the
 * InvitationDto interface that is used to interact with the backend API.
 * It is a service instead of a class with static methods to allow for
 * dependency injection.
 */
@Injectable({
  providedIn: 'root'
})
export class InvitationMapper {
  private _userService: UserService = inject(UserService);

  /**
   * toDto takes an Invitation and returns an InvitationDto
   * @param model the Invitation to convert
   * @returns an InvitationDto
   */
  toDto(model: Invitation): InvitationDto {
    const company = this._userService.company;
    const user = this._userService.user;
    return {
      email: model.email,
      name: model.name,
      businessName: model.businessName,
      role: "borrower",
      isDirect: true,
      senderAccountId: user?.id,
      supplierId: company?.companyId,
      id: Number(model.id), // Convert id to number
    }
  }

  /**
   * toModel takes an InvitationDto and returns an Invitation model
   * @param dto the InvitationDto to convert
   * @returns an Invitation model
   */
  toModel(dto: InvitationDto): Invitation {
    return new Invitation(
      dto.email.trim(), // email
      dto.name.trim(), // name
      // FIXME: API shouldn't accept empty businessName
      dto.businessName?.trim() ?? '', // businessName
      dto.lastSent?.trim() ?? '', // lastSent
      ""+dto.id, // id
      Number(dto.accepted) // accepted
    );
  }
}